import { CSSProperties, ReactNode } from 'react';
import styles from './ProgressBar.module.css';

// THE horizontal progress bar for the whole app — Figma "⚠️ Symphony DS" →
// Progress Bar (node 333:644). Never hand-roll a track + percent-width fill;
// see CLAUDE.md → "Progress bars".
//
// The design is Type × progress, and those are the prop names:
//   type     → 'progress' (a quantity running down/up) | 'password' (strength)
//   progress → the LEVEL, which is what picks the fill colour
// `value` is the fill width in percent — the design's variant widths are sample
// data, so the number always comes from the caller.
export type ProgressBarType = 'progress' | 'password';
export type ProgressLevel = 'success' | 'pending' | 'destructive';
export type PasswordLevel = 'strong' | 'medium' | 'weak';

interface CommonProps {
  /** 0–100. Clamped, so a caller's over-100 ratio can't overflow the track. */
  value: number;
  /** The design's label line above the bar. Omit when the call site renders its own head row. */
  label?: ReactNode;
  /** No role/aria — for a bar whose surrounding text already states the value. */
  decorative?: boolean;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
}

// The two types carry different level vocabularies, so the union stops
// `type="password" progress="pending"` at compile time.
export type ProgressBarProps = CommonProps &
  ({ type?: 'progress'; progress: ProgressLevel } | { type: 'password'; progress: PasswordLevel });

export function ProgressBar(props: ProgressBarProps) {
  const { value, label, decorative, className, style, progress } = props;
  const type = props.type ?? 'progress';
  const pct = Math.min(100, Math.max(0, value));

  const aria = decorative
    ? { 'aria-hidden': true as const }
    : {
        role: 'progressbar' as const,
        'aria-valuenow': Math.round(pct),
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'aria-label': props['aria-label'] ?? (typeof label === 'string' ? label : undefined),
      };

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')} style={style}>
      {label != null && <span className={styles.label}>{label}</span>}
      <div className={styles.track} {...aria}>
        <div className={[styles.fill, styles[`type-${type}`], styles[`level-${progress}`]].join(' ')} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
